'use client';

import classNames from 'classnames';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import TrailPageType from '@alltrails/amplitude/enums/TrailPageType';
import logReviewGPTFeedbackClicked from '@alltrails/amplitude/events/logReviewGPTFeedbackClicked';
import logTrailDetailsActionTapped from '@alltrails/amplitude/events/logTrailDetailsActionTapped';
import useUser from '@alltrails/context/hooks/useUser';
import Typography from '@alltrails/core/components/Typography';
import getFullName from '@alltrails/core/utils/getFullName';
import getScreenReaderRatingText from '@alltrails/core/utils/getScreenReaderRatingText';
import Button from '@alltrails/denali/components/Button';
import Heading from '@alltrails/denali/components/Heading';
import IconButton from '@alltrails/denali/components/IconButton';
import Link from '@alltrails/denali/components/Link';
import LoadingSpinner from '@alltrails/denali/components/LoadingSpinner';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import Hide from '@alltrails/denali/icons/Hide';
import StarFilled from '@alltrails/denali/icons/StarFilled';
import ThumbsDown from '@alltrails/denali/icons/ThumbsDown';
import ThumbsUp from '@alltrails/denali/icons/ThumbsUp';
import FeedbackConfirmationDialog from '@alltrails/layout/components/FeedbackConfirmationDialog';
import { Modal } from '@alltrails/modal';
import type { Review } from '@alltrails/types/Review';
import { useReviewSummaryContext } from '@/contexts/ReviewSummaryContext';
import { REVIEWS_SECTION_ID } from '@/utils/constants';
import cardStyles from '../CardModule/CardModule.module.scss';
import ReviewSummaryAvatars from '../ReviewSummaryAvatars/ReviewSummaryAvatars';
import styles from './ReviewSummary.module.scss';
import useFeedbackProvided from './useFeedbackProvided';

type ReviewSummaryProps = {
  trailId: number;
  shuffledReviews?: Review[];
  isAdmin?: boolean;
  shouldExpand?: boolean;
  isInline?: boolean;
  avgRating?: number;
  trailPageType: TrailPageType;
};

const SUMMARY_TRUNCATE_LENGTH = 250;

const ReviewSummary = ({ trailId, shuffledReviews, isAdmin, shouldExpand, isInline, avgRating = 0, trailPageType }: ReviewSummaryProps) => {
  const user = useUser();
  const intl = useIntl();
  const isMobile = useIsMobileSizedScreen();
  const feedbackKey = useMemo(() => {
    if (user) {
      return `${user.id}-${trailId}`;
    }
    return `${trailId}`;
  }, [user, trailId]);

  const { isLoading: isReviewSummaryLoading, hideReviewSummary, reviewSummary, onToggleHideReviewSummary } = useReviewSummaryContext();

  const [showFullText, setShowFullText] = useState(false);
  const { isFeedbackProvided, provideFeedback } = useFeedbackProvided(feedbackKey, reviewSummary);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

  const closeFeedbackModal = useCallback(() => setIsFeedbackModalOpen(false), []);

  const onClickYes = useCallback(() => {
    setIsFeedbackModalOpen(true);
    provideFeedback();
    logReviewGPTFeedbackClicked({ trail_id: trailId, feedback: 'helpful' });
  }, [provideFeedback, trailId]);

  const onClickNo = useCallback(() => {
    setIsFeedbackModalOpen(true);
    provideFeedback();
    logReviewGPTFeedbackClicked({ trail_id: trailId, feedback: 'not helpful' });
  }, [provideFeedback, trailId]);

  const onShowAllReviewsClick = () => {
    logTrailDetailsActionTapped({
      trail_id: String(trailId),
      trail_page_action: 'show all reviews',
      trail_page_location: 'chat gpt card',
      trail_page_type: trailPageType
    });
    const reviewSection = document.getElementById(REVIEWS_SECTION_ID);
    if (reviewSection) {
      reviewSection.focus();
      reviewSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const avatars = useMemo(() => {
    if (!shuffledReviews) {
      return [];
    }
    return shuffledReviews.map(review => ({
      userId: review.user.id,
      userName: getFullName(review.user)
    }));
  }, [shuffledReviews]);

  const displaySummary = useMemo(() => {
    if (!reviewSummary) return reviewSummary;
    if (shouldExpand || reviewSummary.length <= SUMMARY_TRUNCATE_LENGTH) return reviewSummary;
    return `${reviewSummary.slice(0, SUMMARY_TRUNCATE_LENGTH)}...`;
  }, [reviewSummary, shouldExpand]);

  const wrap = (content: ReactNode) =>
    isInline ? (
      <div className={styles.inline}>{content}</div>
    ) : (
      <div className={cardStyles.card}>
        <div className={cardStyles.inner}>{content}</div>
      </div>
    );

  if (isAdmin && hideReviewSummary) {
    return wrap(
      <div className={styles.adminContainer}>
        {isReviewSummaryLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            <Hide size="lg" />
            <div className={styles.hiddenInfo}>
              <Typography variant="heading300" color="primary">
                <FormattedMessage defaultMessage="Review summary hidden" />
              </Typography>
              <Typography variant="text200" color="primary">
                <FormattedMessage defaultMessage="This summary was hidden by an admin and does not appear on the trail page." />
              </Typography>
            </div>
            <div className={styles.adminAction}>
              <Button text="Restore summary" onClick={onToggleHideReviewSummary} size="sm" variant="elevated" />
            </div>
          </>
        )}
      </div>
    );
  }

  if (isAdmin && !hideReviewSummary && !reviewSummary) {
    return wrap(
      <div className={styles.adminContainer}>
        {isReviewSummaryLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            <div className={styles.hiddenInfo}>
              <Typography variant="heading300" color="primary">
                <FormattedMessage defaultMessage="No review summary available" />
              </Typography>
            </div>
            <div className={styles.adminAction}>
              <Button
                icon={{ Component: Hide }}
                text={<FormattedMessage defaultMessage="Hide summary from trail page" />}
                onClick={onToggleHideReviewSummary}
                size="sm"
                variant="elevated"
              />
            </div>
          </>
        )}
      </div>
    );
  }

  if (hideReviewSummary || !reviewSummary) {
    return null;
  }

  if (isInline) {
    const feedbackInfo = () => (
      <div className={styles.info}>
        <Typography variant="text100" color="secondary">
          <FormattedMessage defaultMessage="This summary is AI-generated from trail reviews." />
          {!isFeedbackProvided && (
            <>
              {' '}
              <FormattedMessage defaultMessage="Was it helpful?" />
            </>
          )}
        </Typography>
        {!isFeedbackProvided && (
          <div className={styles.vote}>
            <IconButton
              icon={{ Component: ThumbsUp, color: '--color-text-secondary' }}
              onClick={onClickYes}
              size="sm"
              title={intl.formatMessage({ defaultMessage: 'Yes' })}
              variant="flat"
            />
            <div className={styles.divider} />
            <IconButton
              icon={{ Component: ThumbsDown, color: '--color-text-secondary' }}
              onClick={onClickNo}
              size="sm"
              title={intl.formatMessage({ defaultMessage: 'No' })}
              variant="flat"
            />
          </div>
        )}
      </div>
    );

    return (
      <>
        {wrap(
          <>
            <ReviewSummaryAvatars avatars={avatars} />
            <div className={styles.contentBlock}>
              <Heading as="h2" size="200">
                <FormattedMessage defaultMessage="Trailgoers are saying" />
              </Heading>
              <Typography variant="text200" color="primary">
                {displaySummary}
              </Typography>
              {feedbackInfo()}
            </div>
            {!shouldExpand && (
              <Button
                className={styles.cta}
                title={intl.formatMessage({ defaultMessage: 'Show all reviews' })}
                text={<FormattedMessage defaultMessage="Show all reviews" />}
                size="md"
                variant="default"
                onClick={onShowAllReviewsClick}
              />
            )}
          </>
        )}
        <FeedbackConfirmationDialog durationMs={2000} isOpen={isFeedbackModalOpen} onRequestClose={closeFeedbackModal} />
      </>
    );
  }

  const showMoreButton = !shouldExpand && reviewSummary.length > SUMMARY_TRUNCATE_LENGTH;
  const reviewSummaryText = showMoreButton ? `${reviewSummary.slice(0, SUMMARY_TRUNCATE_LENGTH)}...` : reviewSummary;

  const cardContent = (summary: string, showMore: boolean, showRating: boolean, showDisclaimer: boolean, inModal: boolean) => (
    <div className={classNames(styles.content, { [styles.modalContent]: inModal })}>
      {showRating ? (
        <div className={styles.avgRating}>
          <Typography variant="heading600">
            <FormattedNumber minimumFractionDigits={1} value={avgRating} />
          </Typography>
          <span role="img" aria-label={getScreenReaderRatingText(intl, avgRating)}>
            <StarFilled color="--color-text-primary" size="lg" />
          </span>
        </div>
      ) : (
        <ReviewSummaryAvatars avatars={avatars} />
      )}
      <div className={styles.summary}>
        <Typography variant="text200" color="primary">
          {summary}
        </Typography>
        {showMore && !shouldExpand && (
          <Link onClick={() => setShowFullText(true)} className={styles.link}>
            <FormattedMessage defaultMessage="more" />
          </Link>
        )}
      </div>
      {(showDisclaimer || !showMore) && (
        <div className={styles.info}>
          <Typography variant="text100" color="secondary">
            <FormattedMessage defaultMessage="This summary is AI-generated from trail reviews." />
          </Typography>
          {!isFeedbackProvided && (
            <div>
              <Typography variant="text100" color="secondary">
                <FormattedMessage defaultMessage="Was it helpful?" />
              </Typography>
              <div className={styles.buttons}>
                <Button className={styles.button} variant="flat" text={<FormattedMessage defaultMessage="Yes" />} onClick={onClickYes} />
                <div className={styles.divider} />
                <Button className={styles.button} variant="flat" text={<FormattedMessage defaultMessage="No" />} onClick={onClickNo} />
              </div>
            </div>
          )}
        </div>
      )}
      {showRating && <ReviewSummaryAvatars avatars={avatars} />}
    </div>
  );

  return (
    <>
      <div className={cardStyles.card}>
        <div className={cardStyles.inner}>
          <Heading as="h2" className={cardStyles.cardTitle} size="300">
            <FormattedMessage defaultMessage="Trailgoers are saying" />
          </Heading>
          {cardContent(reviewSummaryText, showMoreButton, !isMobile, isMobile, false)}
          {(isAdmin || !shouldExpand) && (
            <div className={styles.container}>
              <div className={styles.buttonContainer}>
                <div className={styles.action}>
                  {isAdmin ? (
                    <Button
                      icon={{ Component: Hide }}
                      text={<FormattedMessage defaultMessage="Hide summary from trail page" />}
                      onClick={onToggleHideReviewSummary}
                      size="md"
                      variant="elevated"
                    />
                  ) : (
                    <Button
                      title="Show all reviews"
                      text={<FormattedMessage defaultMessage="Show all reviews" />}
                      size="md"
                      variant="elevated"
                      onClick={onShowAllReviewsClick}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <FeedbackConfirmationDialog durationMs={2000} isOpen={isFeedbackModalOpen} onRequestClose={closeFeedbackModal} />
      <Modal
        isOpen={showFullText}
        onCloseRequest={() => setShowFullText(false)}
        title={<FormattedMessage defaultMessage="Trailgoers are saying " />}
        modalClassName={styles.modal}
      >
        {cardContent(reviewSummary, false, true, true, true)}
      </Modal>
    </>
  );
};

export default ReviewSummary;
